import { useState, useCallback } from 'react';
import { pwg201Api } from '../../../services/pwg201Api';

export function usePWG201Navigation(loadRecord: (id: string) => Promise<void>) {
  const [allIds, setAllIds] = useState<string[]>([]);
  const [currentIdx, setCurrentIdx] = useState<number>(-1);

  const loadList = useCallback(async () => {
    try {
      const res = await pwg201Api.list(1, 1000);
      const ids = (res.data ?? []).map(r => r.fg110_01);
      setAllIds(ids);
      if (ids.length > 0 && currentIdx === -1) {
        setCurrentIdx(0);
        await loadRecord(ids[0]);
      }
    } catch {
      // silent
    }
  }, [loadRecord, currentIdx]);

  const navTo = useCallback(
    async (idx: number) => {
      if (idx < 0 || idx >= allIds.length) return;
      setCurrentIdx(idx);
      await loadRecord(allIds[idx]);
    },
    [allIds, loadRecord]
  );

  const navFirst = () => navTo(0);
  const navPrev = () => navTo(currentIdx - 1);
  const navNext = () => navTo(currentIdx + 1);
  const navLast = () => navTo(allIds.length - 1);

  const addIdToList = useCallback((id: string) => {
    setAllIds(prev => {
      if (prev.includes(id)) return prev;
      const updated = [...prev, id];
      setCurrentIdx(updated.length - 1);
      return updated;
    });
  }, []);

  const removeIdFromList = useCallback(() => {
    setAllIds(prev => {
      const next = prev.filter((_, i) => i !== currentIdx);
      const nextIdx = Math.min(currentIdx, next.length - 1);
      setCurrentIdx(nextIdx);
      return next;
    });
  }, [currentIdx]);

  return {
    allIds,
    currentIdx,
    loadList,
    navTo,
    navFirst,
    navPrev,
    navNext,
    navLast,
    addIdToList,
    removeIdFromList,
  };
}
